import { Fragment, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { AuditoriaRow } from '../../types/database.types';

/**
 * Auditoría (DAF v2, sección 37, regla de negocio 20: "Las modificaciones
 * sensibles deberán conservar usuario y fecha de modificación"). La tabla
 * `auditoria` existía en `schema.sql` desde el inicio del proyecto, con su
 * política RLS admin-only, pero nada escribía en ella — ver
 * `supabase/schema_auditoria.sql` para el trigger genérico que la conecta
 * y `ARQUITECTURA_FRONTEND.md` para el hallazgo completo. Esta pantalla es
 * la otra mitad de esa conexión: sin ella, la evidencia existiría en la
 * base de datos pero nadie en el salón podría consultarla.
 *
 * Solo lectura a propósito — no hay botón de "editar" ni "borrar" un
 * registro de auditoría; eso rompería el propósito de la tabla. La propia
 * RLS de `auditoria` ya lo impide también del lado del servidor (ver
 * `schema_auditoria.sql`): sin política de insert/update/delete para
 * `authenticated`, esas operaciones quedan denegadas por defecto para
 * cualquiera que no sea el trigger mismo.
 */

const TABLAS_AUDITADAS = [
  { value: '', label: 'Todas las tablas' },
  { value: 'usuarios', label: 'Usuarios' },
  { value: 'profesionales', label: 'Profesionales' },
  { value: 'clientes', label: 'Clientas' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'gastos', label: 'Gastos' },
  { value: 'compras', label: 'Compras' },
  { value: 'cuentas_por_pagar', label: 'Cuentas por pagar' },
  { value: 'pagos_proveedores', label: 'Pagos a proveedores' },
  { value: 'pagos_empleados', label: 'Pagos a empleados' },
  { value: 'comisiones', label: 'Comisiones' },
  { value: 'aperturas_caja', label: 'Aperturas de caja' },
  { value: 'movimientos_caja', label: 'Movimientos de caja' },
];

const ACCION_LABEL: Record<string, string> = {
  insert: 'Creación',
  update: 'Modificación',
  delete: 'Eliminación',
};

const ACCION_BADGE: Record<string, string> = {
  insert: 'bg-green-100 text-green-800',
  update: 'bg-amber-100 text-amber-800',
  delete: 'bg-red-100 text-red-700',
};

interface AuditoriaConUsuario extends AuditoriaRow {
  usuarios: { nombre: string } | null;
}

/** Campos que difieren entre el estado anterior y el nuevo — ignora
 * `updated_at` porque cambia en cada UPDATE y no aporta nada al ver el
 * cambio real. */
function camposModificados(antes: Record<string, unknown> | null, despues: Record<string, unknown> | null) {
  if (!antes || !despues) return [];
  const claves = new Set([...Object.keys(antes), ...Object.keys(despues)]);
  claves.delete('updated_at');
  const cambios: { campo: string; antes: unknown; despues: unknown }[] = [];
  for (const campo of claves) {
    const a = antes[campo];
    const d = despues[campo];
    if (JSON.stringify(a) !== JSON.stringify(d)) cambios.push({ campo, antes: a, despues: d });
  }
  return cambios;
}

function formatoValor(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  return String(v);
}

export function AuditoriaList() {
  const [tabla, setTabla] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);

  const { data, loading, error } = useSupabaseQuery<AuditoriaConUsuario[]>(async () => {
    let query = supabase
      .from('auditoria')
      .select('*, usuarios ( nombre )')
      .order('fecha', { ascending: false })
      .limit(200);
    if (tabla) query = query.eq('tabla', tabla);
    const { data, error } = await query;
    return { data: data as unknown as AuditoriaConUsuario[] | null, error };
  }, [tabla]);

  return (
    <div>
      <PageHeader
        title="Auditoría"
        description="Usuario y fecha de cada modificación sensible — DAF v2, sección 37, regla 20"
        actions={
          <select className="input-field" value={tabla} onChange={(e) => setTabla(e.target.value)}>
            {TABLAS_AUDITADAS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        }
      />

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState title="Sin registros de auditoría todavía" description="Aparecerán aquí en cuanto se cree o modifique algo en las tablas auditadas." />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Tabla</th>
                <th className="px-4 py-3 font-medium">Acción</th>
                <th className="px-4 py-3 font-medium">Usuario</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((fila) => {
                const cambios = camposModificados(fila.datos_anteriores, fila.datos_nuevos);
                const abierto = expandido === fila.id;
                return (
                  <Fragment key={fila.id}>
                    <tr className="border-b border-onyx/5 last:border-0">
                      <td className="px-4 py-3 text-onyx/60">
                        {new Date(fila.fecha).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                      <td className="px-4 py-3 text-onyx">{fila.tabla}</td>
                      <td className="px-4 py-3">
                        <span className={`badge ${ACCION_BADGE[fila.accion] ?? 'bg-onyx/5 text-onyx/60'}`}>
                          {ACCION_LABEL[fila.accion] ?? fila.accion}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-onyx/70">{fila.usuarios?.nombre ?? 'Sistema / RPC público'}</td>
                      <td className="px-4 py-3 text-right">
                        {cambios.length > 0 && (
                          <button
                            className="text-xs text-gold-deep hover:underline"
                            onClick={() => setExpandido(abierto ? null : fila.id)}
                          >
                            {abierto ? 'Ocultar' : `Ver cambios (${cambios.length})`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {abierto && cambios.length > 0 && (
                      <tr className="border-b border-onyx/5 bg-onyx/[0.02] last:border-0">
                        <td colSpan={5} className="px-4 py-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-onyx/40">
                                <th className="py-1 pr-4 font-medium">Campo</th>
                                <th className="py-1 pr-4 font-medium">Antes</th>
                                <th className="py-1 font-medium">Después</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cambios.map((c) => (
                                <tr key={c.campo}>
                                  <td className="py-1 pr-4 font-medium text-onyx/70">{c.campo}</td>
                                  <td className="py-1 pr-4 text-onyx/60">{formatoValor(c.antes)}</td>
                                  <td className="py-1 text-onyx">{formatoValor(c.despues)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
